import { useAutoneTranslation } from '@autone/translations';
import { TabsContent, TabsTrigger } from '@radix-ui/react-tabs';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Search } from 'lucide-react';
import {
  forwardRef,
  type HTMLAttributes,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Checkbox,
  cn,
  TailwindButton,
  TailwindInputWithIcon,
} from '../../../tailwind';
import { RippleRoot } from '../../../tailwind/RippleRoot';

import { useGeneralFiltersContext } from './GeneralFilters.context';
import { type GeneralFilterGroup, type GeneralFilterOption } from './types';

interface GeneralFiltersGroupTriggerProps {
  filterGroup: GeneralFilterGroup;
}

export const GeneralFiltersGroupTrigger = ({
  filterGroup,
}: GeneralFiltersGroupTriggerProps) => {
  const { toggledOptions } = useGeneralFiltersContext();
  const { id, translatedDisplayName } = filterGroup;

  const activeOptionsCount = useMemo(
    () =>
      Object.values(toggledOptions[id] || {}).filter((value) => value === true)
        .length,
    [toggledOptions, id],
  );

  return (
    <TabsTrigger
      value={filterGroup.id}
      className="relative overflow-hidden w-full flex justify-between font-medium px-3 py-2 rounded-md data-[state=active]:bg-primary-5 data-[state=active]:text-primary-100 hover:bg-grey-5"
    >
      <span>{translatedDisplayName}</span>
      {activeOptionsCount > 0 && (
        <div
          data-testid={`${filterGroup.id}-active-count`}
          className="grid place-items-center bg-primary-100 text-white aspect-square rounded-sm -me-1 h-full"
        >
          {activeOptionsCount}
        </div>
      )}
    </TabsTrigger>
  );
};

interface GeneralFiltersGroupOptionsProps {
  filterGroup: GeneralFilterGroup;
}

export const GeneralFiltersGroupOptions = ({
  filterGroup,
}: GeneralFiltersGroupOptionsProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { common } = useAutoneTranslation();
  const { toggleFilterGroup, toggledOptions } = useGeneralFiltersContext();
  const { id: filterGroupId, options } = filterGroup;

  const visibleOptions = useMemo(
    () =>
      options.filter((option) =>
        option.translatedDisplayName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      ),
    [options, searchTerm],
  );

  const allVisibleOptionsSelected = useMemo(() => {
    if (visibleOptions.length === 0) return false;
    return visibleOptions.every(
      (option) => toggledOptions?.[filterGroupId]?.[option.id] === true,
    );
  }, [visibleOptions, filterGroupId, toggledOptions]);

  const label = allVisibleOptionsSelected
    ? common('clear-all')
    : common('select-all');

  return (
    <TabsContent
      value={filterGroup.id}
      className="flex flex-col h-full min-h-0 px-5 py-3 gap-4 data-[state=inactive]:hidden"
    >
      {/* Search + select/clear all */}
      <div className="flex flex-row items-center gap-2">
        <TailwindInputWithIcon
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={common('search-placeholder')}
          className="h-8 w-2/3"
          icon={<Search size={16} className="text-grey-80" />}
        />

        <TailwindButton
          className="ms-auto"
          variant={'ghost'}
          aria-label={label}
          onClick={() =>
            toggleFilterGroup(
              filterGroupId,
              !allVisibleOptionsSelected,
              visibleOptions.map((o) => o.id),
            )
          }
        >
          {label}
        </TailwindButton>
      </div>

      {/* Virtualised list container */}
      <GeneralFiltersOptionsList
        filterGroup={filterGroup}
        visibleOptions={visibleOptions}
      />
    </TabsContent>
  );
};

interface GeneralFiltersOptionsListProps
  extends GeneralFiltersGroupTriggerProps {
  visibleOptions: GeneralFilterOption[];
}

const GeneralFiltersOptionsList = ({
  filterGroup,
  visibleOptions,
}: GeneralFiltersOptionsListProps) => {
  const { id: filterGroupId } = filterGroup;

  const parentRef = useRef<HTMLDivElement | null>(null);

  const rowVirtualizer = useVirtualizer({
    count: visibleOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 42,
    overscan: 50,
  });

  return (
    <div
      ref={parentRef}
      className="grow min-h-0 -mb-3 pb-3 overflow-y-scroll no-scrollbar"
    >
      <ul
        className="relative w-full"
        style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const option = visibleOptions[virtualRow.index];
          if (!option) return null;

          return (
            <GeneralFiltersOption
              key={option.id}
              groupId={filterGroupId}
              option={option}
              style={{
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            />
          );
        })}
      </ul>
    </div>
  );
};

interface GeneralFiltersOptionProps extends HTMLAttributes<HTMLLIElement> {
  groupId: string;
  option: GeneralFilterOption;
}

export const GeneralFiltersOption = forwardRef<
  HTMLLIElement,
  GeneralFiltersOptionProps
>(({ groupId, option, className, ...props }, ref) => {
  const { toggleOption, toggledOptions } = useGeneralFiltersContext();
  const optionIsSelected = toggledOptions?.[groupId]?.[option.id] || false;

  const toggleFilterOption = useCallback(
    () => toggleOption(groupId, option.id, !optionIsSelected),
    [toggleOption, groupId, option.id, optionIsSelected],
  );

  return (
    <li
      key={option.id}
      className={cn(
        'absolute overflow-hidden flex flex-row justify-between items-center px-2 py-3 rounded-md hover:bg-grey-5 hover:cursor-pointer',
        className,
      )}
      onClick={toggleFilterOption}
      {...props}
      ref={ref}
    >
      <RippleRoot
        className="bg-grey-60"
        pulseDuration={0.4}
        pulseOpacity={0.3}
      />
      <span className="font-medium">{option.translatedDisplayName}</span>
      <div className="grid place-items-center h-full pe-1">
        <Checkbox checked={optionIsSelected} />
      </div>
    </li>
  );
});

interface SelectedCountBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  count: number;
}

export const SelectedCountBadge = ({
  count,
  className,
  ...props
}: SelectedCountBadgeProps) =>
  count > 0 ? (
    <span
      className={cn(
        'ml-2 inline-flex items-center justify-center rounded bg-primary-100 text-white text-xs px-2 h-5 min-w-5',
        className,
      )}
      {...props}
    >
      {count}
    </span>
  ) : null;

export const GeneralFiltersNoOptions = () => {
  const { common } = useAutoneTranslation();
  return (
    <div className="flex flex-col justify-center items-center self-stretch w-full gap-1">
      <p className="text-primary-100 text-lg font-medium">
        {common('filters.general-filters-no-options.header')}
      </p>
      <p className="text-grey-60">
        {common('filters.general-filters-no-options.subtitle')}
      </p>
    </div>
  );
};
